import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ageFromDateOfBirth, getAgeRangeDescription } from "./ageUtils";
import { RangeSlider } from "./RangeSlider";
import { useIsMobile } from "./useIsMobile";

function clampAge(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

export function AgeRangePicker({
  userAge,
  ageYounger,
  ageOlder,
  preferMobileControls = false,
  onChangeYounger,
  onChangeOlder,
}: {
  userAge?: number;
  ageYounger: number;
  ageOlder: number;
  preferMobileControls?: boolean;
  onChangeYounger: (value: number) => void;
  onChangeOlder: (value: number) => void;
}) {
  const mobile = useIsMobile();
  const useMobileControls = mobile || preferMobileControls;
  const validUserAge = userAge && userAge >= 18 ? userAge : 25;
  const validAgeYounger = ageYounger ?? 5;
  const validAgeOlder = ageOlder ?? 10;

  const propsMinAge = validUserAge - validAgeYounger;
  const propsMaxAge = validUserAge + validAgeOlder;
  const sliderMin = Math.max(18, validUserAge - 10);
  const sliderMax = validUserAge + 10;

  const [displayRange, setDisplayRange] = useState({ min: propsMinAge, max: propsMaxAge });
  const [editing, setEditing] = useState<"min" | "max" | null>(null);
  const [draft, setDraft] = useState("");
  const minInputRef = useRef<HTMLInputElement>(null);
  const maxInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisplayRange({ min: propsMinAge, max: propsMaxAge });
  }, [propsMinAge, propsMaxAge]);

  const description = useMemo(
    () => getAgeRangeDescription(displayRange.min, displayRange.max, validUserAge),
    [displayRange.max, displayRange.min, validUserAge],
  );

  const applyRange = (minAge: number, maxAge: number) => {
    const low = clampAge(Math.min(minAge, maxAge), sliderMin, sliderMax);
    const high = clampAge(Math.max(minAge, maxAge), sliderMin, sliderMax);
    setDisplayRange({ min: low, max: high });
    onChangeYounger(validUserAge - low);
    onChangeOlder(high - validUserAge);
  };

  const focusInput = (field: "min" | "max") => {
    const input = field === "min" ? minInputRef.current : maxInputRef.current;
    input?.focus();
    input?.select();
  };

  const beginEdit = (field: "min" | "max", immediateFocus = false) => {
    const value = field === "min" ? displayRange.min : displayRange.max;
    const applyEditState = () => {
      setEditing(field);
      setDraft(String(value));
    };

    if (immediateFocus) {
      flushSync(applyEditState);
      focusInput(field);
      return;
    }

    applyEditState();
    requestAnimationFrame(() => {
      focusInput(field);
    });
  };

  const commitEdit = (field: "min" | "max") => {
    const parsed = Number.parseInt(draft, 10);
    if (!Number.isNaN(parsed)) {
      if (field === "min") {
        applyRange(parsed, displayRange.max);
      } else {
        applyRange(displayRange.min, parsed);
      }
    }
    setEditing(null);
    setDraft("");
  };

  if (!useMobileControls) {
    return (
      <div className="age-range-picker age-range-picker--desktop">
        <div className="inline-fields preferences-inline-fields">
          <label className="field">
            <span>Years younger</span>
            <input
              type="number"
              min={0}
              max={validUserAge - 18}
              value={ageYounger}
              onChange={(event) => onChangeYounger(Number(event.target.value))}
            />
          </label>
          <label className="field">
            <span>Years older</span>
            <input
              type="number"
              min={0}
              max={10}
              value={ageOlder}
              onChange={(event) => onChangeOlder(Number(event.target.value))}
            />
          </label>
        </div>
        <p className="age-range-picker-note">{description}</p>
      </div>
    );
  }

  const renderAgeBox = (field: "min" | "max") => {
    const inputRef = field === "min" ? minInputRef : maxInputRef;
    const label = field === "min" ? "Min age" : "Max age";
    const value = field === "min" ? displayRange.min : displayRange.max;

    return (
      <div
        className={`age-range-box ${editing === field ? "active" : ""}`}
        onPointerDown={(event) => {
          if (editing !== field) {
            if (editing) commitEdit(editing);
            event.preventDefault();
            beginEdit(field, true);
          }
        }}
        onClick={() => {
          if (editing !== field) beginEdit(field);
        }}
      >
        <span className="age-range-box-label">{label}</span>
        <input
          ref={inputRef}
          className="age-range-box-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          enterKeyHint="done"
          aria-label={label}
          maxLength={2}
          value={editing === field ? draft : String(value)}
          onFocus={() => {
            if (editing !== field) beginEdit(field);
          }}
          onChange={(event) => setDraft(event.target.value.replace(/\D/g, "").slice(0, 2))}
          onBlur={() => commitEdit(field)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              inputRef.current?.blur();
            }
          }}
        />
      </div>
    );
  };

  return (
    <div className="age-range-picker">
      <div className="age-range-display">
        {renderAgeBox("min")}
        {renderAgeBox("max")}
      </div>

      <RangeSlider
        min={sliderMin}
        max={sliderMax}
        low={displayRange.min}
        high={displayRange.max}
        onChange={applyRange}
        ariaLabel="Preferred age range"
      />

      <p className="age-range-picker-note">{description}</p>
    </div>
  );
}

export { ageFromDateOfBirth };
